/**
 * GlobalView — Rail Layer Renderer
 *
 * Renders train entities on the Cesium globe as billboards.
 * Uses disableDepthTestDistance to ensure visibility without
 * any expensive height sampling. Simple and performant.
 */

import * as Cesium from 'cesium';
import { useEntityStore, selectTrains } from '../../stores/entityStore';
import { useGlobeStore } from '../../stores/globeStore';
import { snapToTrack } from './railTrackRenderer';
import { getTrainIconDataUri } from './trainIcons';
import type { Entity as GVEntity } from '@globalview/shared';

// ─── State ───────────────────────────────────────────────────────

let viewer: Cesium.Viewer | null = null;
let billboards: Cesium.BillboardCollection | null = null;
let labels: Cesium.LabelCollection | null = null;
let unsubEntity: (() => void) | null = null;
let unsubGlobe: (() => void) | null = null;

const billboardMap = new Map<string, Cesium.Billboard>();
const labelMap = new Map<string, Cesium.Label>();

// ─── Render Helpers ──────────────────────────────────────────────

function updateEntity(entity: GVEntity, visible: boolean, labelsVisible: boolean): void {
  if (!billboards || !labels || !viewer) return;

  const category = (entity.properties as Record<string, unknown>)?.category as string ?? '';
  const trainName = (entity.properties as Record<string, unknown>)?.trainName as string ?? '';

  const snapped = snapToTrack(entity.position.lat, entity.position.lon);
  // Use a reasonable Swiss altitude (won't be exact but disableDepthTestDistance
  // ensures visibility). Most Swiss cities are 300-600m above ellipsoid.
  const position = Cesium.Cartesian3.fromDegrees(snapped.lon, snapped.lat, 450);

  // ─── Billboard ───────────────────────────────────────────
  let bb = billboardMap.get(entity.id);

  if (bb) {
    bb.position = position;
    bb.show = visible;
  } else {
    bb = billboards.add({
      position,
      image: getTrainIconDataUri(category),
      scale: 0.7,
      show: visible,
      // Always render on top — no depth testing against 3D tiles
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      heightReference: Cesium.HeightReference.NONE,
      id: entity.id,
    });
    billboardMap.set(entity.id, bb);
  }

  // ─── Label ───────────────────────────────────────────────
  let lbl = labelMap.get(entity.id);

  if (lbl) {
    lbl.position = position;
    lbl.text = trainName;
    lbl.show = visible && labelsVisible;
  } else {
    lbl = labels.add({
      position,
      text: trainName,
      font: '20px monospace',
      fillColor: Cesium.Color.WHITE,
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      pixelOffset: new Cesium.Cartesian2(8, -8),
      show: visible && labelsVisible,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      heightReference: Cesium.HeightReference.NONE,
      distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 500_000),
    });
    labelMap.set(entity.id, lbl);
  }
}

function removeEntity(id: string): void {
  const bb = billboardMap.get(id);
  if (bb && billboards) {
    billboards.remove(bb);
    billboardMap.delete(id);
  }

  const lbl = labelMap.get(id);
  if (lbl && labels) {
    labels.remove(lbl);
    labelMap.delete(id);
  }
}

// ─── Sync (throttled) ────────────────────────────────────────────

let syncTimer: ReturnType<typeof setTimeout> | null = null;

function requestSync(): void {
  if (syncTimer) return;
  syncTimer = setTimeout(() => {
    syncTimer = null;
    doSync();
  }, 500);
}

function doSync(): void {
  if (!viewer || viewer.isDestroyed()) return;

  const trains = selectTrains(useEntityStore.getState());
  const { railLayerVisible, railLabelsVisible } = useGlobeStore.getState();

  const activeIds = new Set<string>();

  for (const entity of trains) {
    activeIds.add(entity.id);
    updateEntity(entity, railLayerVisible, railLabelsVisible);
  }

  for (const id of billboardMap.keys()) {
    if (!activeIds.has(id)) {
      removeEntity(id);
    }
  }

  viewer.scene.requestRender();
}

// ─── Public API ──────────────────────────────────────────────────

export function initRailLayer(v: Cesium.Viewer): void {
  viewer = v;

  billboards = new Cesium.BillboardCollection({ scene: v.scene });
  labels = new Cesium.LabelCollection({ scene: v.scene });

  v.scene.primitives.add(billboards);
  v.scene.primitives.add(labels);

  unsubEntity = useEntityStore.subscribe(requestSync);

  let prevRailVis = useGlobeStore.getState().railLayerVisible;
  let prevLabelVis = useGlobeStore.getState().railLabelsVisible;
  unsubGlobe = useGlobeStore.subscribe((state) => {
    if (state.railLayerVisible !== prevRailVis || state.railLabelsVisible !== prevLabelVis) {
      prevRailVis = state.railLayerVisible;
      prevLabelVis = state.railLabelsVisible;
      doSync();
    }
  });

  console.log('[RailLayer] Initialized');
}

export function destroyRailLayer(): void {
  unsubEntity?.();
  unsubGlobe?.();
  unsubEntity = null;
  unsubGlobe = null;

  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }

  if (viewer && !viewer.isDestroyed()) {
    if (billboards) viewer.scene.primitives.remove(billboards);
    if (labels) viewer.scene.primitives.remove(labels);
  }

  billboards = null;
  labels = null;
  billboardMap.clear();
  labelMap.clear();
  viewer = null;

  console.log('[RailLayer] Destroyed');
}

export function highlightRailEntity(id: string | null): void {
  for (const [, bb] of billboardMap) {
    bb.scale = 0.7;
  }

  if (id) {
    const bb = billboardMap.get(id);
    if (bb) {
      bb.scale = 1.1;
    }
  }

  viewer?.scene.requestRender();
}

/**
 * Get the current rendered Cartesian3 position of a rail billboard.
 * Used by SelectionMarker to track the actual rendered position
 * (which includes snapToTrack offset + 450m altitude).
 */
export function getRailBillboardPosition(entityId: string): Cesium.Cartesian3 | null {
  const bb = billboardMap.get(entityId);
  if (!bb) return null;
  return bb.position as Cesium.Cartesian3;
}
